import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { api } from "./lib/api";
import Home from "./pages/Home";
import QuoteWizard from "./pages/QuoteWizard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminQuoteDetail from "./pages/AdminQuoteDetail";
import IndicacaoRef from "./pages/IndicacaoRef";
import ClubeLanding from "./pages/clube/ClubeLanding";
import ClubeCadastro from "./pages/clube/ClubeCadastro";
import ClubeLogin from "./pages/clube/ClubeLogin";
import ClubeRecuperar from "./pages/clube/ClubeRecuperar";
import ClubeRedefinir from "./pages/clube/ClubeRedefinir";
import ClubePainel from "./pages/clube/ClubePainel";
import AdminIndicadores from "./pages/AdminIndicadores";
import AdminIndicadorPerfil from "./pages/AdminIndicadorPerfil";
import AdminSettings from "./pages/AdminSettings";
import AdminReservas from "./pages/AdminReservas";
import AdminDrivers from "./pages/AdminDrivers";
import AdminJobs from "./pages/AdminJobs";
import DriverApp from "./pages/DriverApp";
import ClientArea from "./pages/ClientArea";
import AdminLogin from "./pages/AdminLogin";
import AdminStats from "./pages/AdminStats";
import AdminAdmins from "./pages/AdminAdmins";
import AdminRecovery, { AdminResetPassword } from "./pages/AdminRecovery";
import AdminPricing from "./pages/AdminPricing";
import AdminServicos from "./pages/AdminServicos";
import AdminLogs from "./pages/AdminLogs";
import AdminNotifications from "./pages/AdminNotifications";
import AdminTerms from "./pages/AdminTerms";
import AdminClientes from "./pages/AdminClientes";
import AdminAgenda from "./pages/AdminAgenda";
import MinhaConta from "./pages/MinhaConta";
import AdminDivulgacao from "./pages/AdminDivulgacao";

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    const destino = location.pathname || "/";
    if (!sid) {
      navigate(destino, { replace: true });
      return;
    }
    api
      .post("/auth/session", { session_id: sid })
      .then((res) => {
        window.history.replaceState(null, "", destino);
        navigate(destino, { state: { user: res.data.user }, replace: true });
      })
      .catch(() => navigate(destino, { replace: true }));
  }, [navigate, location]);

  return (
    <div className="min-h-screen bg-[#0b1220] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-slate-300 mt-4 text-sm">Entrando...</p>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/orcamento" element={<QuoteWizard />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/orcamento/:quoteId" element={<AdminQuoteDetail />} />
      <Route path="/admin/reservas" element={<AdminReservas />} />
      <Route path="/admin/motoristas" element={<AdminDrivers />} />
      <Route path="/admin/corridas" element={<AdminJobs />} />
      <Route path="/admin/config" element={<AdminSettings />} />
      <Route path="/painel-gestao" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminStats />} />
      <Route path="/admin/admins" element={<AdminAdmins />} />
      <Route path="/admin/recuperar-senha" element={<AdminRecovery />} />
      <Route path="/admin/resetar-senha" element={<AdminResetPassword />} />
      <Route path="/admin/precos" element={<AdminPricing />} />
      <Route path="/admin/servicos" element={<AdminServicos />} />
      <Route path="/admin/logs" element={<AdminLogs />} />
      <Route path="/admin/notificacoes" element={<AdminNotifications />} />
      <Route path="/admin/termos" element={<AdminTerms />} />
      <Route path="/admin/clientes" element={<AdminClientes />} />
      <Route path="/admin/agenda" element={<AdminAgenda />} />
      <Route path="/admin/divulgacao" element={<AdminDivulgacao />} />
      <Route path="/motorista" element={<DriverApp />} />
      <Route path="/meus-servicos" element={<ClientArea />} />
      <Route path="/minha-conta" element={<MinhaConta />} />
      <Route path="/clube" element={<ClubeLanding />} />
      <Route path="/clube/cadastro" element={<ClubeCadastro />} />
      <Route path="/clube/login" element={<ClubeLogin />} />
      <Route path="/clube/recuperar" element={<ClubeRecuperar />} />
      <Route path="/clube/redefinir" element={<ClubeRedefinir />} />
      <Route path="/clube/painel" element={<ClubePainel />} />
      <Route path="/ind/:codigo" element={<IndicacaoRef />} />
      <Route path="/admin/indicadores" element={<AdminIndicadores />} />
      <Route path="/admin/indicadores/:indicadorId" element={<AdminIndicadorPerfil />} />
    </Routes>
  );
}

function SplashScreen({ fading }) {
  const [src, setSrc] = useState(`${process.env.REACT_APP_BACKEND_URL}/api/public/splash`);
  return (
    <div className="fixed inset-0 z-[9999] bg-[#0a1f44] flex items-center justify-center transition-opacity duration-500"
      style={{ opacity: fading ? 0 : 1 }} data-testid="splash-screen">
      <img src={src} onError={() => { if (src !== "/splash.jpg") setSrc("/splash.jpg"); }}
        alt="Carlos Jr Fretes e Mudanças" className="w-full h-full object-cover md:object-contain" />
    </div>
  );
}

function App() {
  const [splash, setSplash] = useState(true);
  const [splashFading, setSplashFading] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setSplashFading(true), 3500);
    const t2 = setTimeout(() => setSplash(false), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <div className="App">
      {splash && <SplashScreen fading={splashFading} />}
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
